/* ============================================================
   SettleDialogs — CashTenderDialog + SplitPaymentDialog.

   These two are tightly coupled: the till's settle button opens
   one or the other depending on the chosen payment method
   (Cash / EFTPOS → CashTenderDialog, Split → SplitPaymentDialog).
   Co-located here so all settle-flow code lives in one navigable
   place.

   Extracted from POSPage.jsx (Feb 2026) — third step of the
   POSPage refactor described in PRD.md → P2 backlog. Behaviour
   and every data-testid are unchanged from the in-file version
   so the existing pytest + Playwright suite keeps working.
   ============================================================ */
import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";

/* -----------------------------------------------------------
   CashTenderDialog — owner ask Feb 2026: when staff tap Cash the
   POS must ask how much the customer handed over and then show
   the change due (like a real POS). Big touch buttons + quick
   denomination chips + Exact for no-change sales. Confirm fires
   the order send with `{ cashReceived }` so the backend stores
   the tender + change on the order/payment_transaction row.

   Extended Feb 2026 (settle discount picker): the dialog now
   also handles EFTPOS settle. In EFTPOS mode, the cash-tender
   block is hidden and only the discount picker + PIN gate +
   confirm button render. The 10% / 20% pills + manager PIN
   prompt are shared between modes — the backend enforces the
   same permission matrix on every settle.
   ----------------------------------------------------------- */
export function CashTenderDialog({
  open,
  onClose,
  total,
  onConfirm,
  title,
  allowKeepOnTab = false,
  confirmLabel,
  paymentMode = "cash", // "cash" | "eftpos"
  allowDiscount = false,
  cashOnTabAvailable = 0, // surplus credit already paid into the tab
  refundDue = 0, // owner ask Feb 2026 — surplus tab credit to hand back as cash
  allowKeepSeated = false, // owner ask Feb 2026 — table stays occupied after settle
  allowShortPayment = false, // owner ask Feb 2026 — short pay auto-detected when tendered < total (no toggle, see `isShort` below)
}) {
  // Input as a STRING so the user can be mid-typing "12." without
  // React flipping it to a number and erasing the trailing dot.
  const [tendered, setTendered] = useState("");
  const [keepOnTab, setKeepOnTab] = useState(false);
  const [keepSeated, setKeepSeated] = useState(false);
  // Manual discount + manager PIN state. `discountPct` is one of
  // 0 / 10 / 20. PIN required for 10% EFTPOS, 20% on any method.
  const [discountPct, setDiscountPct] = useState(0);
  const [managerPin, setManagerPin] = useState("");

  React.useEffect(() => {
    if (open) {
      // EFTPOS defaults to the discounted total (typical case = pay in
      // full). Cash starts blank so staff has to type the tender.
      setTendered(paymentMode === "eftpos" ? Math.max(0, total).toFixed(2) : "");
      setKeepOnTab(false);
      setKeepSeated(false);
      setDiscountPct(0);
      setManagerPin("");
    }
    // We intentionally only re-init when `open` flips. The eslint hook
    // dep rule would have us include `total`/`paymentMode` but doing so
    // would wipe the user's typed amount the moment the parent re-fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // If the EFTPOS discount changes mid-flow, sync the default back to
  // the new discounted total — but only if the user hasn't started
  // editing (i.e., the field still equals the previous discountedTotal-
  // ish value). Simpler proxy: bump it whenever the discount changes.
  React.useEffect(() => {
    if (open && paymentMode === "eftpos") {
      setTendered(Math.max(0, Math.round((total - (discountPct > 0 ? total * (discountPct / 100) : 0)) * 100) / 100).toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountPct]);

  // Discount maths
  const discountAmount = useMemo(
    () => (discountPct > 0 ? Math.round(total * (discountPct / 100) * 100) / 100 : 0),
    [total, discountPct]
  );
  const discountedTotal = Math.max(0, Math.round((total - discountAmount) * 100) / 100);
  // Permission matrix — mirrors backend DISCOUNT_PIN_MATRIX.
  const pinRequired = useMemo(() => {
    if (discountPct === 0) return false;
    if (paymentMode === "eftpos") return true; // 10% and 20% on EFTPOS both need PIN
    // cash: 10% no PIN, 20% PIN
    return discountPct === 20;
  }, [paymentMode, discountPct]);
  const pinSatisfied = !pinRequired || managerPin.trim().length >= 4;

  const tenderedNum = parseFloat(tendered) || 0;
  const change = tenderedNum - discountedTotal;
  // Owner ask Feb 2026 (rev2): short pay is now AUTO-DETECTED — no
  // checkbox. Any time staff confirms with `tenderedNum < discountedTotal`
  // we route the partial amount through the topup endpoint (tab stays
  // open). When `tenderedNum >= discountedTotal` we fall through to the
  // regular settle endpoint (tab closes, change handed back).
  const isShort = allowShortPayment && tenderedNum > 0 && tenderedNum < discountedTotal - 0.005;
  const cashValid = tenderedNum > 0 && (
    isShort
      ? true
      : tenderedNum >= discountedTotal - 0.005
  );
  // Refund-only mode: total is $0 (or near-zero) AND there's a surplus
  // credit to hand back. Till just needs to confirm the cash drawer
  // opened and they handed over `refundDue` to the customer. No tender
  // input required.
  const refundOnly = refundDue > 0.005 && discountedTotal < 0.005;
  const valid = refundOnly
    ? pinSatisfied
    : paymentMode === "eftpos"
      ? (tenderedNum > 0 && pinSatisfied)
      : (cashValid && pinSatisfied);

  const quickAdd = (amt) => {
    setTendered((prev) => {
      const cur = parseFloat(prev) || 0;
      return (cur + amt).toFixed(2);
    });
  };

  const setExact = () => setTendered(discountedTotal.toFixed(2));

  const submit = () => {
    // Both modes now send the typed amount. The receiver inspects
    // `shortPay` (derived from `isShort`) to decide whether to settle
    // (close the tab) or top up (keep it open).
    onConfirm(tenderedNum, {
      keepOnTab,
      keepSeated,
      shortPay: isShort,
      discountPct,
      managerPin: pinRequired ? managerPin.trim() : "",
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-zinc-950 border border-white/10 max-w-md text-white"
        data-testid="pos-cash-tender-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-white">
            {title || (paymentMode === "eftpos" ? "EFTPOS payment" : "Cash payment")}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          {/* Total due — large, gold */}
          <div className="flex items-center justify-between bg-zinc-900/60 border border-white/10 rounded-md px-4 py-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans">
                {discountPct > 0 ? "After discount" : "Total due"}
              </p>
              {discountPct > 0 && (
                <p className="text-[10px] text-zinc-500 font-sans mt-0.5 line-through">
                  ${total.toFixed(2)}
                </p>
              )}
            </div>
            <span className="font-mono text-2xl text-gold" data-testid="pos-cash-total">
              ${discountedTotal.toFixed(2)}
            </span>
          </div>

          {/* Discount picker — owner ask Feb 2026: 10% / 20% pills at settle.
              Overrides the auto cash-10 promo when staff picks one. */}
          {allowDiscount && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans">
                Apply discount
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { pct: 0, label: "None" },
                  { pct: 10, label: "10% Off" },
                  { pct: 20, label: "20% Off" },
                ].map(({ pct, label }) => {
                  const active = discountPct === pct;
                  return (
                    <button
                      key={pct}
                      type="button"
                      onClick={() => { setDiscountPct(pct); setManagerPin(""); }}
                      className={`h-11 rounded-md border text-sm font-sans uppercase tracking-[0.1em] transition ${
                        active
                          ? "bg-gold/15 border-gold/60 text-gold"
                          : "bg-zinc-900 border-white/10 text-zinc-300 hover:border-gold/30"
                      }`}
                      data-testid={`pos-discount-${pct}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {pinRequired && (
                <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/[0.06] px-3 py-2.5">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-sans mb-1">
                    Manager PIN required
                  </p>
                  <input
                    type="password"
                    value={managerPin}
                    onChange={(e) => setManagerPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                    placeholder="••••"
                    autoComplete="off"
                    className="w-full h-11 px-3 bg-zinc-900 border border-amber-500/40 rounded-md text-white text-lg font-mono tracking-[0.4em] focus:outline-none focus:border-amber-400"
                    data-testid="pos-manager-pin"
                  />
                  <p className="text-[10px] text-amber-300/70 font-sans mt-1.5">
                    {discountPct}% off on {paymentMode === "eftpos" ? "EFTPOS" : "Cash"} needs a manager's PIN.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Refund-due banner — owner ask Feb 2026: when paid_amount on
              the tab exceeds the bill, surface the surplus the till must
              hand back. Shows in BOTH cash + EFTPOS modes (cash is the
              physical refund, EFTPOS settle of a credit-positive tab also
              hands back cash from the drawer). */}
          {refundDue > 0.005 && (
            <div className="rounded-md border border-sky-500/50 bg-sky-500/[0.08] px-4 py-3 flex items-center justify-between" data-testid="pos-refund-banner">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-sky-300 font-sans">
                  Refund to customer
                </p>
                <p className="text-[10px] text-sky-300/70 font-sans mt-0.5">
                  Hand back from cash drawer
                </p>
              </div>
              <span className="font-mono text-2xl text-sky-300" data-testid="pos-refund-amount">
                ${refundDue.toFixed(2)}
              </span>
            </div>
          )}

          {/* Tender input — always rendered in cash and EFTPOS modes
              (hidden only in refund-only mode). Owner ask Feb 2026
              (rev2): when staff types less than the total due we
              auto-detect a short payment and the tab stays open. No
              checkbox needed. */}
          {!refundOnly && (
            <>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans mb-1.5">
                  {paymentMode === "eftpos" ? "EFTPOS amount" : "Cash received"}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gold text-xl font-mono">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={tendered}
                    onChange={(e) => setTendered(e.target.value)}
                    placeholder="0.00"
                    autoFocus
                    className="w-full h-14 pl-8 pr-3 bg-zinc-900 border border-white/15 rounded-md text-white text-3xl font-mono focus:outline-none focus:border-gold"
                    data-testid="pos-cash-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-6 gap-2">
                {[5, 10, 20, 50, 100].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => quickAdd(d)}
                    className="h-10 rounded-md border border-white/10 bg-zinc-900 hover:border-gold/40 hover:bg-gold/[0.08] text-white text-sm font-mono transition"
                    data-testid={`pos-cash-quick-${d}`}
                  >
                    +${d}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={setExact}
                  className="h-10 rounded-md border border-gold/40 bg-gold/[0.08] hover:bg-gold/15 text-gold text-[11px] font-sans uppercase tracking-[0.1em] transition"
                  data-testid="pos-cash-exact"
                >
                  Exact
                </button>
              </div>

              {/* Status banner — auto-routing math:
                    • isShort  → purple "Will stay on tab · $X left"
                    • !isShort & cashValid → emerald "Change due / Charge $X"
                    • else (no tender / over) → rose placeholder */}
              <div
                className={`flex items-center justify-between rounded-md px-4 py-3 border ${
                  isShort
                    ? "border-purple-500/40 bg-purple-500/[0.06]"
                    : cashValid
                      ? "border-emerald-500/40 bg-emerald-500/[0.06]"
                      : "border-rose-500/30 bg-rose-500/[0.04]"
                }`}
              >
                <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-sans">
                  {isShort
                    ? "Short pay · tab stays open"
                    : cashValid
                      ? paymentMode === "eftpos" ? "Charge customer" : "Change due"
                      : tendered ? "Enter a positive amount" : "Awaiting tender"}
                </span>
                <span
                  className={`font-mono text-2xl ${
                    isShort
                      ? "text-purple-300"
                      : cashValid ? "text-emerald-400" : "text-rose-400"
                  }`}
                  data-testid="pos-cash-change"
                >
                  {isShort
                    ? `$${(discountedTotal - tenderedNum).toFixed(2)} left`
                    : cashValid
                      ? paymentMode === "eftpos"
                        ? `$${tenderedNum.toFixed(2)}`
                        : `$${change.toFixed(2)}`
                      : "—"}
                </span>
              </div>

              {/* Keep change on tab — cash only, only when there IS
                  change to keep (i.e., over-tendered cash). Short-pay
                  rides on the topup endpoint already so this toggle is
                  irrelevant in that mode. */}
              {allowKeepOnTab && paymentMode === "cash" && !isShort && cashValid && change > 0.001 && (
                <label
                  className="flex items-start gap-2.5 px-3 py-2.5 rounded-md border border-sky-500/40 bg-sky-500/[0.06] cursor-pointer hover:bg-sky-500/[0.10] transition"
                  data-testid="pos-cash-keep-on-tab"
                >
                  <input
                    type="checkbox"
                    checked={keepOnTab}
                    onChange={(e) => setKeepOnTab(e.target.checked)}
                    className="mt-0.5 accent-sky-400"
                    data-testid="pos-cash-keep-on-tab-checkbox"
                  />
                  <span className="text-sm text-sky-100 font-sans leading-tight">
                    <span className="font-semibold">Keep ${change.toFixed(2)} on the tab</span>
                    <span className="block text-[11px] text-sky-300/80 mt-0.5">
                      Tab stays open · change becomes credit for the next order
                    </span>
                  </span>
                </label>
              )}
            </>
          )}
        </div>

        {/* "Guests staying for now" — owner ask Feb 2026. Lets the till
            settle the bill BUT keep the table marked Occupied so the
            party can stay (another round, finish their drink, just
            chatting). Staff hits "Clear Table" from the floor view
            when they actually leave. Only renders when the caller
            explicitly enables it (settle flows — not top-ups). */}
        {allowKeepSeated && (
          <label
            className="flex items-start gap-2.5 px-3 py-2.5 mt-3 rounded-md border border-amber-500/40 bg-amber-500/[0.06] cursor-pointer hover:bg-amber-500/[0.10] transition"
            data-testid="pos-keep-seated-toggle"
          >
            <input
              type="checkbox"
              checked={keepSeated}
              onChange={(e) => setKeepSeated(e.target.checked)}
              className="mt-0.5 accent-amber-400"
              data-testid="pos-keep-seated-checkbox"
            />
            <span className="text-sm text-amber-100 font-sans leading-tight">
              <span className="font-semibold">Guests staying for now</span>
              <span className="block text-[11px] text-amber-300/80 mt-0.5">
                Bill closes · table stays Occupied · clear it manually when they leave
              </span>
            </span>
          </label>
        )}

        <div className="grid grid-cols-2 gap-2 mt-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="h-12 border-white/15 text-zinc-300 hover:bg-white/5 hover:text-white"
            data-testid="pos-cash-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!valid}
            className={`h-12 disabled:opacity-40 text-white font-sans uppercase tracking-[0.12em] truncate px-3 ${
              isShort
                ? "bg-purple-600 hover:bg-purple-500"
                : paymentMode === "eftpos"
                  ? "bg-blue-600 hover:bg-blue-500"
                  : "bg-emerald-600 hover:bg-emerald-500"
            }`}
            data-testid="pos-cash-confirm"
          >
            {confirmLabel
              ? confirmLabel
              : refundOnly
                ? `Refund $${refundDue.toFixed(2)} & Close`
                : isShort
                  ? `Short pay $${tenderedNum.toFixed(2)}`
                  : paymentMode === "eftpos"
                    ? `Run EFTPOS · $${tenderedNum.toFixed(2)}`
                    : `Confirm $${tenderedNum > 0 ? tenderedNum.toFixed(2) : "—"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* -----------------------------------------------------------
   SplitPaymentDialog — owner ask Feb 2026 (revised twice).
   • Apply 0% / 10% / 20% discount on the WHOLE bill, then split.
   • Customers can pay LESS than the bill (tab stays open) or MORE
     (extra stays as credit on the tab) when they're still seated.
   • Cash received is PRE-FILLED with the cash portion so staff
     don't have to hit "Exact" first (the previous version showed
     "$X" as placeholder which staff mistook for the actual value
     and the Confirm button stayed silently disabled).
   ----------------------------------------------------------- */
export function SplitPaymentDialog({ open, onClose, total, onConfirm, title, allowKeepSeated = false }) {
  const [cashPart, setCashPart] = useState("");
  const [eftposPart, setEftposPart] = useState("");
  const [discountPct, setDiscountPct] = useState(0); // 0 | 10 | 20
  const [managerPin, setManagerPin] = useState("");
  const [cashTendered, setCashTendered] = useState("");
  const [keepSeated, setKeepSeated] = useState(false);
  // Owner ask Feb 2026 — short / over pay on a split when guests are
  // still seated. ON → tab stays open and we accept any non-zero sum
  // (short pays the difference later, over-tenders leave credit on
  // the tab for the next round).
  const [partialPay, setPartialPay] = useState(false);

  // Discount maths — applied to the WHOLE bill, then split.
  const splitDiscount = useMemo(
    () => (discountPct > 0 ? Math.round(total * (discountPct / 100) * 100) / 100 : 0),
    [total, discountPct]
  );
  const discountedTotal = useMemo(
    () => Math.max(0, Math.round((total - splitDiscount) * 100) / 100),
    [total, splitDiscount]
  );

  // Reset on open. Default to a 50/50 split; cash received pre-filled
  // with the cash portion so confirm is enabled out of the box.
  React.useEffect(() => {
    if (open) {
      const half = Math.round((total / 2) * 100) / 100;
      const otherHalf = Math.max(0, Math.round((total - half) * 100) / 100);
      setCashPart(half.toFixed(2));
      setEftposPart(otherHalf.toFixed(2));
      setDiscountPct(0);
      setManagerPin("");
      setCashTendered(half.toFixed(2));
      setKeepSeated(false);
      setPartialPay(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When the discount changes mid-flow, rebalance to 50/50 of the new
  // discounted total AND pre-fill the cash drawer field to match.
  React.useEffect(() => {
    if (!open) return;
    const half = Math.round((discountedTotal / 2) * 100) / 100;
    const otherHalf = Math.max(0, Math.round((discountedTotal - half) * 100) / 100);
    setCashPart(half.toFixed(2));
    setEftposPart(otherHalf.toFixed(2));
    setCashTendered(half.toFixed(2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountPct]);

  // Auto-sync cash received whenever the cash portion changes. Staff
  // can still override after — typing in the cashTendered field stops
  // overriding it (sync is one-way: cashPart → cashTendered).
  React.useEffect(() => {
    setCashTendered(cashPart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashPart]);

  const pinRequired = discountPct > 0;
  const pinSatisfied = !pinRequired || managerPin.trim().length >= 4;

  const cashNum = parseFloat(cashPart) || 0;
  const eftposNum = parseFloat(eftposPart) || 0;
  const sumOfParts = Math.round((cashNum + eftposNum) * 100) / 100;
  // Full-settle mode requires sum == discountedTotal. Partial-pay
  // mode (guests staying) allows ANY non-zero sum.
  const totalsMatch = partialPay
    ? sumOfParts > 0
    : Math.abs(sumOfParts - discountedTotal) < 0.01;

  const tenderedNum = parseFloat(cashTendered) || 0;
  const cashChange = Math.max(0, Math.round((tenderedNum - cashNum) * 100) / 100);
  const cashValid = cashNum === 0 ? true : tenderedNum >= cashNum - 0.005;
  const valid = totalsMatch && cashValid && cashNum >= 0 && eftposNum >= 0 && sumOfParts > 0 && pinSatisfied;

  // Pretty diff vs the bill (only meaningful in partial mode).
  const diff = Math.round((sumOfParts - discountedTotal) * 100) / 100; // +ve = over, -ve = short

  const submit = () => {
    onConfirm({
      splitCashAmount: cashNum,
      splitEftposAmount: eftposNum,
      splitDiscountPct: discountPct,
      managerPin: pinRequired ? managerPin.trim() : "",
      cashReceived: tenderedNum,
      keepSeated,
      // partialPay → backend routes EACH leg through the topup
      // endpoint instead of settle, so the tab stays open.
      partialPay,
    });
  };

  const applyPreset = (cashPct) => {
    const c = Math.round((discountedTotal * cashPct) * 100) / 100;
    const e = Math.max(0, Math.round((discountedTotal - c) * 100) / 100);
    setCashPart(c.toFixed(2));
    setEftposPart(e.toFixed(2));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-zinc-950 border border-white/10 max-w-md text-white max-h-[92vh] overflow-y-auto"
        data-testid="pos-split-payment-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-white">
            {title || "Split payment — Cash + EFTPOS"}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <div className="flex items-center justify-between bg-zinc-900/60 border border-white/10 rounded-md px-4 py-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans">
                {discountPct > 0 ? "After discount" : "Tab outstanding"}
              </p>
              {discountPct > 0 && (
                <p className="text-[10px] text-zinc-500 font-sans mt-0.5 line-through">
                  ${total.toFixed(2)}
                </p>
              )}
            </div>
            <span className="font-mono text-2xl text-gold" data-testid="pos-split-total">
              ${discountedTotal.toFixed(2)}
            </span>
          </div>

          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans">
              Apply discount on whole bill
            </p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { pct: 0, label: "None" },
                { pct: 10, label: "10% Off" },
                { pct: 20, label: "20% Off" },
              ].map(({ pct, label }) => {
                const active = discountPct === pct;
                return (
                  <button
                    key={pct}
                    type="button"
                    onClick={() => { setDiscountPct(pct); setManagerPin(""); }}
                    className={`h-11 rounded-md border text-sm font-sans uppercase tracking-[0.1em] transition ${
                      active
                        ? "bg-gold/15 border-gold/60 text-gold"
                        : "bg-zinc-900 border-white/10 text-zinc-300 hover:border-gold/30"
                    }`}
                    data-testid={`pos-split-discount-${pct}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {pinRequired && (
              <div className="mt-1 rounded-md border border-amber-500/40 bg-amber-500/[0.06] px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-sans mb-1">
                  Manager PIN required
                </p>
                <input
                  type="password"
                  value={managerPin}
                  onChange={(e) => setManagerPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                  placeholder="••••"
                  autoComplete="off"
                  className="w-full h-11 px-3 bg-zinc-900 border border-amber-500/40 rounded-md text-white text-lg font-mono tracking-[0.4em] focus:outline-none focus:border-amber-400"
                  data-testid="pos-split-manager-pin"
                />
              </div>
            )}
          </div>

          {/* Partial-pay toggle — owner ask Feb 2026. ON allows the
              split sum to be MORE or LESS than the bill; tab stays
              open in either case. */}
          <label
            className="flex items-start gap-2.5 px-3 py-2.5 rounded-md border border-purple-500/40 bg-purple-500/[0.08] cursor-pointer hover:bg-purple-500/[0.14] transition"
            data-testid="pos-split-partial-toggle"
          >
            <input
              type="checkbox"
              checked={partialPay}
              onChange={(e) => setPartialPay(e.target.checked)}
              className="mt-0.5 accent-purple-400"
              data-testid="pos-split-partial-checkbox"
            />
            <span className="text-sm text-purple-100 font-sans leading-tight">
              <span className="font-semibold">Guests still seated — tab stays open</span>
              <span className="block text-[11px] text-purple-300/80 mt-0.5">
                Pay less (rest stays owed) or more (rest stays as credit) · no need to balance
              </span>
            </span>
          </label>

          <div className="grid grid-cols-4 gap-2">
            {[
              { lbl: "50/50", pct: 0.5 },
              { lbl: "60/40", pct: 0.6 },
              { lbl: "40/60", pct: 0.4 },
              { lbl: "All Cash", pct: 1 },
            ].map(({ lbl, pct }) => (
              <button
                key={lbl}
                type="button"
                onClick={() => applyPreset(pct)}
                className="h-9 rounded-md border border-white/10 bg-zinc-900 hover:border-gold/40 hover:bg-gold/[0.06] text-zinc-300 text-[11px] font-sans uppercase tracking-[0.08em] transition"
                data-testid={`pos-split-preset-${lbl.replace(/\W+/g, '')}`}
              >
                {lbl}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-sans mb-1.5">
                Cash portion
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-300 text-lg font-mono">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={cashPart}
                  onChange={(e) => setCashPart(e.target.value)}
                  className="w-full h-12 pl-8 pr-3 bg-zinc-900 border border-emerald-500/30 rounded-md text-white text-xl font-mono focus:outline-none focus:border-emerald-400"
                  data-testid="pos-split-cash"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-blue-300 font-sans mb-1.5">
                EFTPOS portion
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300 text-lg font-mono">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={eftposPart}
                  onChange={(e) => setEftposPart(e.target.value)}
                  className="w-full h-12 pl-8 pr-3 bg-zinc-900 border border-blue-500/30 rounded-md text-white text-xl font-mono focus:outline-none focus:border-blue-400"
                  data-testid="pos-split-eftpos"
                />
              </div>
            </div>
          </div>

          {/* Status banner — different copy for full vs partial mode. */}
          <div className={`rounded-md px-3 py-2 border flex items-center justify-between ${
            partialPay
              ? (sumOfParts > 0
                  ? "border-purple-500/40 bg-purple-500/[0.05]"
                  : "border-zinc-700 bg-zinc-900/40")
              : (totalsMatch
                  ? "border-emerald-500/40 bg-emerald-500/[0.05]"
                  : "border-rose-500/40 bg-rose-500/[0.05]")
          }`}>
            <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-sans">
              {partialPay
                ? (diff > 0.005 ? "Over · stays on tab" : diff < -0.005 ? "Short · stays owed" : "Exact")
                : (totalsMatch ? "Split balanced" : "Cash + EFTPOS must equal bill")}
            </span>
            <span className={`font-mono text-sm ${
              partialPay
                ? (diff !== 0 ? "text-purple-300" : "text-emerald-300")
                : (totalsMatch ? "text-emerald-300" : "text-rose-300")
            }`}>
              {partialPay && diff !== 0
                ? `${diff > 0 ? '+' : ''}$${diff.toFixed(2)} (${sumOfParts.toFixed(2)} / ${discountedTotal.toFixed(2)})`
                : `$${sumOfParts.toFixed(2)} / $${discountedTotal.toFixed(2)}`}
            </span>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans mb-1.5">
              Cash received (drawer)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gold text-lg font-mono">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={cashTendered}
                onChange={(e) => setCashTendered(e.target.value)}
                className="w-full h-12 pl-8 pr-3 bg-zinc-900 border border-white/15 rounded-md text-white text-xl font-mono focus:outline-none focus:border-gold"
                data-testid="pos-split-cash-received"
              />
            </div>
            <button
              type="button"
              onClick={() => setCashTendered(cashNum.toFixed(2))}
              className="mt-2 text-[10px] text-gold underline-offset-2 hover:underline font-sans uppercase tracking-[0.12em]"
              data-testid="pos-split-cash-exact"
            >
              Exact · ${cashNum.toFixed(2)}
            </button>
          </div>

          {cashTendered && cashNum > 0 && (
            <div className={`flex items-center justify-between rounded-md px-3 py-2 border ${cashValid ? "border-emerald-500/40 bg-emerald-500/[0.05]" : "border-rose-500/40 bg-rose-500/[0.05]"}`}>
              <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-sans">
                {cashValid ? "Change due (cash)" : "Cash short"}
              </span>
              <span className={`font-mono text-lg ${cashValid ? "text-emerald-300" : "text-rose-300"}`} data-testid="pos-split-change">
                ${cashChange.toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {/* "Guests staying for now" toggle removed from Split (Feb 2026
            owner ask): the partial-pay toggle above already covers the
            "tab stays open" case, and having both labelled with "Guests"
            confused staff. The regular Cash / EFTPOS settle dialog
            still exposes keep-seated for the full-settle path. */}

        <div className="grid grid-cols-2 gap-2 mt-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="h-12 border-white/15 text-zinc-300 hover:bg-white/5 hover:text-white"
            data-testid="pos-split-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!valid}
            className={`h-12 disabled:opacity-40 text-white font-sans uppercase tracking-[0.12em] truncate px-3 ${
              partialPay ? "bg-purple-600 hover:bg-purple-500" : "bg-purple-600 hover:bg-purple-500"
            }`}
            data-testid="pos-split-confirm"
          >
            {!totalsMatch
              ? (partialPay ? "Enter an amount" : "Balance cash + EFTPOS first")
              : !cashValid
                ? "Cash tendered < cash portion"
                : pinRequired && !pinSatisfied
                  ? "Enter manager PIN"
                  : partialPay
                    ? `Take $${sumOfParts.toFixed(2)} · tab open`
                    : `Settle Split · $${discountedTotal.toFixed(2)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
